import { Component } from '@theme/component';

/**
 * @typedef {object} EngravingRefs
 * @property {HTMLInputElement} choice
 * @property {HTMLInputElement[]} choices
 * @property {HTMLInputElement} text
 * @property {HTMLInputElement} fallback
 * @property {HTMLElement} details
 * @property {HTMLElement} counter
 * @property {HTMLElement} error
 */
/** @extends Component<EngravingRefs> */
class LC_Engraving extends Component {
  #pasteController;
  #bindPaste() {
    this.#pasteController?.abort();
    this.#pasteController = new AbortController();
    this.refs.text.addEventListener('paste', (event) => this.handlePaste(event), { signal: this.#pasteController.signal });
  }
  connectedCallback() {
    super.connectedCallback();
    this.refs.choice.disabled = false;
    this.refs.choices.forEach(input => { input.disabled = false; });
    this.refs.fallback.disabled = true;
    this.#bindPaste();
    this.handleChoice();
  }
  updatedCallback() {
    super.updatedCallback();
    this.refs.choice.disabled = false;
    this.refs.choices.forEach(input => { input.disabled = false; });
    this.refs.fallback.disabled = true;
    this.#bindPaste();
    this.handleChoice();
  }
  disconnectedCallback() {
    this.#pasteController?.abort();
    super.disconnectedCallback();
  }
  handleChoice() {
    const selected = this.refs.choices.find(input => input.checked);
    this.refs.choice.value = selected?.value || 'No engraving';
    const enabled = this.refs.choice.value === 'Free engraving';
    this.dataset.enabled = String(enabled);
    this.refs.details.hidden = !enabled;
    this.refs.text.disabled = !enabled;
    this.refs.text.required = enabled;
    if (!enabled) this.refs.text.value = '';
    this.handleInput();
  }
  handleInput() {
    this.refs.text.setCustomValidity('');
    this.refs.counter.textContent = (this.dataset.counter || '[count]/10').replace('[count]', String(this.refs.text.value.length));
    this.validate(false);
  }
  /** @param {ClipboardEvent} event */
  handlePaste(event) {
    const pasted = event.clipboardData?.getData('text') || '';
    const input = this.refs.text;
    const next = input.value.slice(0, input.selectionStart || 0) + pasted + input.value.slice(input.selectionEnd || 0);
    if (!/^[\x20-\x7E]*$/.test(pasted) || next.length > 10) {
      event.preventDefault();
      this.#showError(this.dataset.invalid || 'Invalid engraving text.');
      input.reportValidity();
    }
  }
  /** @param {boolean} [report] @returns {boolean} */
  validate(report = false) {
    const input = this.refs.text;
    let message = input.validationMessage && input.validity.customError ? input.validationMessage : '';
    if (!input.disabled && !message) {
      if (!input.value.trim()) message = this.dataset.empty || 'Enter engraving text.';
      else if (!/^[\x20-\x7E]{1,10}$/.test(input.value)) message = this.dataset.invalid || 'Invalid engraving text.';
    }
    if (input.disabled) message = '';
    this.#showError(message);
    if (message && report) input.reportValidity();
    return !message;
  }
  /** @param {string} message */
  #showError(message) {
    this.refs.text.setCustomValidity(message);
    this.refs.text.setAttribute('aria-invalid', String(Boolean(message)));
    this.refs.error.textContent = message;
    this.refs.error.hidden = !message;
  }
}
if (!customElements.get('lc-engraving')) customElements.define('lc-engraving', LC_Engraving);
